<script setup lang="ts">
import {onMounted, onUnmounted, ref, useTemplateRef} from "vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {storeToRefs} from "pinia";
import {useClipboard, useDateFormat, useNow} from "@vueuse/core";
import Translator from "@/utils/common/Translator.ts";
import DefaultApplicationInfo from "@/utils/common/DefaultApplicationInfo.ts";

const {lastServerLog} = storeToRefs(useErrorsStore())
const {serverLogDialogVisible} = storeToRefs(useErrorsStore())

const dialog = ref(false);
const {copy, copied} = useClipboard()
const serverLog = useTemplateRef<HTMLTextAreaElement | null>("serverLog")

function exportToTextFile() {
  if (!lastServerLog.value.length)
    return;
  const currentDate = useDateFormat(useNow(), "YYYYMMDD.HHmm.ss")
  const customerName = DefaultApplicationInfo.calcCustomerName()
  const blob = new Blob([lastServerLog.value], {type: "text/plain"})
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "ServerLog_" + customerName + "_" + currentDate.value + ".txt"
  link.click();
  URL.revokeObjectURL(link.href)
}

function copyAndSelectServerLog() {
  if (!lastServerLog.value)
    return;
  if (!serverLog.value)
    return;
  copy(lastServerLog.value)
  serverLog.value.select();
}

onMounted(() => {
  serverLogDialogVisible.value = true;
})
onUnmounted(() => {
  serverLogDialogVisible.value = false;
})
</script>
<template>
  <q-dialog v-model="dialog" transition-hide="jump-down" transition-show="jump-up" dir="ltr">
    <q-card class="tw-relative dark:tw-bg-namaBackground-deep-dark tw-rounded-md tw-w-[450px] tw-max-w-[50em]" dir="ltr">
      <q-toolbar
        class="tw-flex tw-sticky tw-left-0 tw-translate-x-0 !tw-rounded-none tw-items-center tw-px-4 tw-justify-between tw-bg-primary-light tw-text-white">
        <q-toolbar-title class="tw-flex tw-items-center tw-gap-2">
          <NamaIcon default-icon="insert_drive_file" class="active-white"/>
          <span v-text="' Server Log'"/>
        </q-toolbar-title>
        <div class="tw-flex tw-items-center tw-gap-1">
          <q-avatar square :title="Translator.translateId('export')" @click.stop="exportToTextFile" class="tw-size-5 tw-cursor-pointer">
            <NamaIcon default-icon="nm-download" class="tw-size-5 active-white"/>
          </q-avatar>
          <q-avatar square :title="Translator.translateId('copy')" @click.stop="copyAndSelectServerLog" class="tw-size-5 tw-cursor-pointer">
            <NamaIcon v-if="copied" default-icon="nm-clipboard-check" class="tw-size-5 active-white"/>
            <NamaIcon v-else default-icon="nm-copy-to-clipboard" class="tw-size-5 active-white"/>
          </q-avatar>
          <q-avatar square class="tw-w-5 tw-cursor-pointer" @click="dialog=false">
            <NamaIcon default-icon="close" class="active-white"/>
          </q-avatar>
        </div>
      </q-toolbar>
      <q-card-section class="!tw-p-2 tw-w-full">
        <textarea ref="serverLog" v-text="lastServerLog" dir="ltr" rows="16"
                  class="tw-w-full tw-rounded-sm tw-text-[#001350] dark:tw-text-white/70 tw-resize-none tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-bright-dark focus:tw-ring-0
                  tw-outline-none tw-text-xs focus:!tw-border-stone-300 !tw-border-stone-300 dark:focus:tw-border-neutral-600 tw-border dark:tw-border-neutral-600"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
